import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ApiService } from '../api.service';
import { McpContextService } from '../mcp-context.service';
import { McpRealtimeStatus, McpTranscriptEntry } from '../mcp-realtime.service';
import { McpTraceService } from '../mcp-trace.service';
import { PageActionService } from '../page-action.service';
import { McpIntentRouterService } from '../mcp-intent-router.service';
import { RealtimeSegment } from './realtime-segment.types';

/**
 * GrokRealtimeSegment
 *
 * xAI (Grok) Voice Agent segment. xAI's realtime API is OpenAI-Realtime
 * compatible but runs over a raw WebSocket, so this class is a hybrid:
 *  - Transport / audio / wake-word / end-of-visit plumbing mirrors the Gemini
 *    segment (getUserMedia → PCM16 capture, base64 playback, wake word, silence
 *    nudge, session refresh, end_conversation).
 *  - The wire protocol (session.update, input_audio_buffer.append, tool calls
 *    via response.function_call_arguments.done, function_call_output) matches
 *    the OpenAI Realtime events used by McpRealtimeService.
 *
 * Auth uses an ephemeral token minted by the backend (POST /mcp/grok/live-config
 * → xAI /v1/realtime/client_secrets), passed on the socket via the
 * `xai-client-secret.<token>` subprotocol so the real API key never reaches the
 * browser.
 */
@Injectable({ providedIn: 'root' })
export class GrokRealtimeSegment implements RealtimeSegment {
    readonly id = 'grok' as const;

    private readonly _status = new BehaviorSubject<McpRealtimeStatus>('idle');
    private readonly _transcript = new BehaviorSubject<McpTranscriptEntry[]>([]);
    private readonly _aiSpeaking = new BehaviorSubject<boolean>(false);
    private readonly _micMuted = new BehaviorSubject<boolean>(true);
    // Local browser-side "user is talking right now" state, purely for the
    // kiosk card pulse. Driven by RMS from the same mic buffer we already send
    // to xAI — no extra graph nodes, no rAF loop, no touch of the LLM path.
    private readonly _userSpeaking = new BehaviorSubject<boolean>(false);
    // Hysteresis counters so a soft syllable or a room-noise blip doesn't flip
    // the pulse. Numbers picked for ~85 ms audio frames (48000/4096):
    //   - go ON after 1 loud frame   (~85 ms)  → immediate visual feedback
    //   - go OFF after 6 quiet frames (~510 ms) → holds the glow between words
    // Thresholds are DELIBERATELY very low — with browser AGC now disabled,
    // some laptop mics run much quieter than the classic 0.03–0.05 speech RMS,
    // and we care more about "any voice-like signal" than a precise
    // speech-vs-noise gate. If it starts pulsing on room noise, raise these.
    private _userSpeakingLoudFrames = 0;
    private _userSpeakingQuietFrames = 0;
    private static readonly USER_SPEAKING_ON_RMS = 0.008;
    private static readonly USER_SPEAKING_OFF_RMS = 0.004;
    private static readonly USER_SPEAKING_ON_FRAMES = 1;
    private static readonly USER_SPEAKING_OFF_FRAMES = 6;

    private _ws: WebSocket | null = null;
    private _micStream: MediaStream | null = null;
    private _inputAudioCtx: AudioContext | null = null;
    private _inputSource: MediaStreamAudioSourceNode | null = null;
    private _inputProcessor: ScriptProcessorNode | null = null;
    private _outputAudioCtx: AudioContext | null = null;
    private _nextPlaybackTime = 0;

    private _connectTimeout: any = null;
    private _connectAttemptId = 0;
    private _autoReconnect = true;
    private _reconnectTimer: any = null;
    private _pendingUnmuteAfterReconnect = false;

    private _wakeWordRecognition: any = null;
    private _sessionRefreshTimer: any = null;
    private _silenceNudgeTimer: any = null;
    private _silenceNudgeCount = 0;
    private _lastModelResponseAt = 0;

    private _assistantTurnText = '';
    private _activeResponses = 0;
    private _userTurnActive = false;
    private _anonymousUserTurns = 0;
    private _lastUserTranscript = '';
    private _toolCallInFlight = false;
    private _playbackEndTimer: any = null;
    private _requireWakeWordOnConnect = false;
    private _micStartTimer: any = null;
    private _responseFallbackTimer: any = null;

    // Live board sync: while a visit is active, mirror kiosk data changes into
    // the conversation so the model never answers from a stale snapshot.
    private _liveContextSub: Subscription | null = null;
    private _liveContextTimer: any = null;
    private _lastLiveContextPush = 0;
    private _lastLiveContextText = '';

    private static readonly WAKE_WORDS = ['yo', 'computer', 'hey assistant', 'a assistant', 'hey system', 'hey grok'];
    private static readonly INPUT_SAMPLE_RATE = 24000;
    private static readonly OUTPUT_SAMPLE_RATE = 24000;
    private static readonly SILENCE_NUDGE_MS = 7000;
    private static readonly MAX_SILENCE_NUDGES = 2;
    private static readonly SESSION_REFRESH_MS = 13 * 60 * 1000;
    // Coalesce a burst of board changes into at most one live push per window.
    private static readonly LIVE_CONTEXT_MIN_INTERVAL_MS = 2500;
    private static readonly KIOSK_TURN_REFRESH = 6;
    private static readonly POST_PLAYBACK_GAP_S = 0.3;
    private static readonly CONNECT_TIMEOUT_MS = 45000;
    private static readonly TOOL_DISPATCH_TIMEOUT_MS = 12000;
    private static readonly TOOL_DISPATCH_SLOW_TIMEOUT_MS = 20000;

    constructor(
        private readonly zone: NgZone,
        private readonly api: ApiService,
        private readonly pageActionService: PageActionService,
        private readonly contextService: McpContextService,
        private readonly traceService: McpTraceService,
        private readonly intentRouter: McpIntentRouterService
    ) {}

    get status$(): Observable<McpRealtimeStatus> {
        return this._status.asObservable();
    }
    get transcript$(): Observable<McpTranscriptEntry[]> {
        return this._transcript.asObservable();
    }
    get aiSpeaking$(): Observable<boolean> {
        return this._aiSpeaking.asObservable();
    }
    get micMuted$(): Observable<boolean> {
        return this._micMuted.asObservable();
    }
    get userSpeaking$(): Observable<boolean> {
        return this._userSpeaking.asObservable();
    }

    prepareKioskSession(requireWakeWord: boolean): void {
        this._requireWakeWordOnConnect = requireWakeWord;
    }

    async start(): Promise<void> {
        if (this._status.value === 'connecting' || this._status.value === 'active') {
            return;
        }
        this._autoReconnect = true;

        const snapshot = this.contextService.getSnapshot();
        if (!this.canStartOnCurrentPage(snapshot)) {
            this._status.next('idle');
            this.addTranscript('system', 'Grok assistant is available on the kiosk Packages station only.');
            return;
        }

        this._status.next('connecting');
        const attemptId = ++this._connectAttemptId;
        this.armConnectTimeout(attemptId);

        try {
            const promptContext = this.contextService.buildPromptContext(snapshot);

            const cfg: any = await this.api
                .postApi('mcp/grok/live-config', {
                    context: {
                        prompt_context: promptContext,
                        route: snapshot.route,
                        page_label: snapshot.pageLabel,
                        user_name: snapshot.userName,
                        user_role: snapshot.role,
                        anonymous: snapshot.anonymous,
                    },
                })
                .toPromise();

            const token = String(cfg?.ephemeral_token ?? '').trim();
            const model = String(cfg?.model ?? 'grok-voice-latest');
            if (!token) {
                throw new Error('Invalid Grok live config response (missing ephemeral_token)');
            }

            const url = `wss://api.x.ai/v1/realtime?model=${encodeURIComponent(model)}`;
            const ws = new WebSocket(url, [`xai-client-secret.${token}`]);
            this._ws = ws;

            ws.onopen = () => {
                this.zone.run(() => {
                    if (attemptId !== this._connectAttemptId) return;
                    this.clearConnectTimeout();
                    this.sendSessionConfig(cfg);
                    this.ensureOutputAudioContext();
                    this.armSessionRefresh();
                    this.subscribeLiveContext();
                    this._status.next('active');
                    this._aiSpeaking.next(false);

                    if (this._pendingUnmuteAfterReconnect) {
                        this._pendingUnmuteAfterReconnect = false;
                        this._micMuted.next(false);
                        this.stopWakeWordListener();
                        this.addTranscript('system', 'Session refreshed — still listening.');
                    } else {
                        this._requireWakeWordOnConnect = false;
                        this._micMuted.next(true);
                        this.startWakeWordListener();
                        this.addTranscript('system', 'Ready — say "Yo" to begin.');
                    }

                    // Acquire the mic AFTER the socket is open — isOpen() checks
                    // both readyState AND status === 'active', so calling this
                    // before onopen returns early and the mic never starts.
                    void this.startMicStreaming().catch((err) => {
                        console.error('[Grok] mic acquisition failed:', err);
                        this.addTranscript(
                            'system',
                            'Microphone permission was denied — allow the mic in the browser and reload.',
                        );
                    });
                });
            };

            ws.onmessage = (ev: MessageEvent) => this.handleServerMessage(ev);

            ws.onerror = (e: any) => {
                this.zone.run(() => {
                    if (attemptId !== this._connectAttemptId) return;
                    console.error('[Grok] socket error:', e);
                });
            };

            ws.onclose = (ev: CloseEvent) => {
                this.zone.run(() => {
                    if (attemptId !== this._connectAttemptId) return;
                    this.clearConnectTimeout();
                    this.cancelSessionRefresh();
                    this.cancelSilenceNudge();
                    console.warn('[Grok] socket closed:', ev?.code, ev?.reason);
                    this._ws = null;
                    this._aiSpeaking.next(false);
                    this._activeResponses = 0;
                    this._status.next('idle');
                    if (this._autoReconnect) {
                        this.addTranscript('system', 'Session ended — reconnecting...');
                        this.scheduleAutoReconnect();
                    }
                });
            };

        } catch (err: any) {
            if (attemptId !== this._connectAttemptId) return;
            this.clearConnectTimeout();
            this._status.next('error');
            this.addTranscript('system', 'Grok failed to start. Please retry.');
            this.traceService.log(
                'grok_start_failed',
                { error: err?.message ?? String(err) },
                { detail: 'Failed to start Grok Voice Agent' }
            );
        }
    }

    async prewarm(): Promise<void> {
        if (this._status.value === 'active' || this._status.value === 'connecting') {
            return;
        }
        this._autoReconnect = true;
        await this.start();
    }

    stop(): void {
        this._autoReconnect = false;
        this.teardown();
        this._status.next('idle');
        this._micMuted.next(true);
    }

    setMicMuted(muted: boolean): void {
        if (this._micMuted.value === muted) return;
        this._micMuted.next(muted);
        if (this._micStartTimer) {
            clearTimeout(this._micStartTimer);
            this._micStartTimer = null;
        }
        // Mic stream stays open for the whole session; we just gate whether
        // audio is sent to the API.
        if (muted) {
            // No more audio frames will be processed until unmute, so the
            // "user is talking" pulse must drop immediately (otherwise the
            // last state persists forever).
            this.resetUserSpeaking();
            this.playMuteTone();
            this.startWakeWordListener();
        } else {
            this.stopWakeWordListener();
        }
    }

    toggleMic(): void {
        this.setMicMuted(!this._micMuted.value);
    }

    sendText(text: string): void {
        const trimmed = String(text ?? '').trim();
        if (!trimmed || !this.isOpen()) return;
        this.addTranscript('user', trimmed);
        this.sendClientText(trimmed);
    }

    // --- Wire protocol -----------------------------------------------------

    private sendSessionConfig(cfg: any): void {
        const voice = String(cfg?.voice ?? 'eve');
        const instructions = String(cfg?.system_instruction ?? '');
        const tools = Array.isArray(cfg?.tools) ? cfg.tools : [];
        // Ship the MINIMUM shape from the xAI Voice Agent docs' baseline example
        // (agent-tools/2f5678db-e740-4ed1-af0f-023538e31fdb.txt, "session.update"
        // sample around lines 182-216). Empirically — see GROK_VOICE_DEBUG_SUMMARY.md
        // attempts #4 and #6 — the moment we include `audio.input.transcription`
        // (with OR without a paired audio.input.format), xAI stops emitting
        // `input_audio_buffer.speech_started`. So we do NOT set the audio block
        // at all: xAI falls back to its default (audio/pcm @ 24 kHz), which is
        // exactly what our capture pipeline resamples to (INPUT_SAMPLE_RATE),
        // and server VAD fires as intended.
        // Trade-off: `conversation.item.input_audio_transcription.updated`
        // events (the live user caption stream) are only emitted when
        // `audio.input.transcription.model = grok-transcribe` is set — which is
        // precisely what breaks VAD. Live user captions in the transcript panel
        // are dropped; assistant transcript still flows via
        // response.output_audio_transcript.delta.
        // `turn_detection.threshold: 0.3` is a small safety net: the docs default
        // is unspecified but reference implementations commonly use 0.5, which
        // combined with browser AGC/noise suppression may under-trigger for a
        // quiet kiosk operator. 0.3 keeps VAD responsive on soft speech; if it
        // over-triggers on ambient noise, raise it in one step.
        this.send({
            type: 'session.update',
            session: {
                instructions,
                voice,
                turn_detection: {
                    type: 'server_vad',
                    threshold: 0.3,
                },
                tools,
            },
        });
        console.log('[Grok] -> session.update sent (voice=%s, tools=%d, threshold=0.3, no audio block)', voice, tools.length);
    }

    private handleServerMessage(ev: MessageEvent): void {
        this.zone.run(() => {
            let msg: any;
            try {
                msg = JSON.parse(ev.data);
            } catch {
                return;
            }

            // Keep only the events that matter for diagnosing the VAD/turn-taking
            // pipeline. Errors + session.updated stay so config rejections are
            // obvious; VAD lifecycle events stay so we can immediately see whether
            // Step 1's minimal session.update restored server VAD.
            if (msg.type === 'error') {
                console.error('[Grok]<- error', msg);
            } else if (msg.type === 'session.updated') {
                console.log('[Grok]<- session.updated (config accepted)', msg?.session ?? msg);
            } else if (
                msg.type === 'input_audio_buffer.speech_started' ||
                msg.type === 'input_audio_buffer.speech_stopped' ||
                msg.type === 'input_audio_buffer.committed' ||
                msg.type === 'response.created'
            ) {
                console.log('[Grok]<-', msg.type);
            }

            switch (msg.type) {
                case 'response.created':
                    // Auto-VAD response arrived — cancel the safety-net trigger.
                    this.clearResponseFallback();
                    this._activeResponses++;
                    this._aiSpeaking.next(true);
                    this._assistantTurnText = '';
                    // A model turn is starting, so the next user speech begins a
                    // fresh user turn rather than extending the previous one.
                    this._userTurnActive = false;
                    break;

                // Server VAD committed the user's turn. Per xAI docs a response
                // is created automatically with server_vad, but if it doesn't
                // arrive shortly we explicitly request one (docs: "response.create
                // — Request the server to create a new assistant response. This is
                // handled automatically when using server-side VAD.").
                case 'input_audio_buffer.committed':
                    this._anonymousUserTurns++;
                    this.scheduleResponseFallback();
                    break;

                case 'response.output_audio.delta':
                case 'response.audio.delta': {
                    const b64 = String(msg.delta ?? '').trim();
                    if (b64) {
                        this._lastModelResponseAt = Date.now();
                        this.cancelSilenceNudge();
                        this.playPcm16Base64(b64, GrokRealtimeSegment.OUTPUT_SAMPLE_RATE);
                    }
                    break;
                }

                case 'response.output_audio_transcript.delta':
                case 'response.audio_transcript.delta': {
                    const frag = String(msg.delta ?? '');
                    if (frag) {
                        this._assistantTurnText += frag;
                        this.upsertAssistantTurn(this._assistantTurnText);
                    }
                    break;
                }

                case 'response.output_audio_transcript.done':
                case 'response.audio_transcript.done': {
                    const full = String(msg.transcript ?? '').trim();
                    if (full) this.upsertAssistantTurn(full);
                    this._assistantTurnText = '';
                    break;
                }

                // xAI renames OpenAI's ...transcription.delta to ...updated and
                // sends the CUMULATIVE transcript each time (may include
                // corrections), so we upsert the in-progress user turn.
                case 'conversation.item.input_audio_transcription.updated': {
                    const t = String(msg.transcript ?? '').trim();
                    if (t && !this._micMuted.value) {
                        this.upsertUserTurn(t);
                        this.armSilenceNudge();
                    }
                    break;
                }

                case 'conversation.item.input_audio_transcription.completed': {
                    const t = String(msg.transcript ?? '').trim();
                    if (t && !this._micMuted.value) {
                        this.upsertUserTurn(t);
                        this.armSilenceNudge();
                    }
                    this._userTurnActive = false;
                    break;
                }

                case 'response.function_call_arguments.done':
                    this.handleFunctionCall(msg);
                    break;

                case 'response.done':
                case 'response.cancelled':
                case 'response.failed':
                case 'response.incomplete':
                    this._activeResponses = Math.max(0, this._activeResponses - 1);
                    if (this._activeResponses === 0) {
                        this._assistantTurnText = '';
                        this.syncAiSpeakingState();
                    }
                    break;

                case 'error':
                    console.error('[Grok] error event:', msg?.error ?? msg);
                    this.traceService.log('grok_error', { error: msg?.error ?? msg }, { detail: 'Grok socket error event' });
                    break;
            }
        });
    }

    private async handleFunctionCall(msg: any): Promise<void> {
        this._toolCallInFlight = true;
        this.cancelSilenceNudge();
        try {
            const callId = String(msg?.call_id ?? '');
            const name = String(msg?.name ?? '');
            let args: Record<string, any> = {};
            try {
                args = JSON.parse(msg?.arguments || '{}');
            } catch {}

            const isPageAction = name === 'page_action';
            const action = String((isPageAction ? args?.action : name) ?? '').trim();
            const rawParams = isPageAction ? { ...args } : { action: name, params: args };
            delete (rawParams as any).action;
            const params = this.flattenActionParamWrappers(
                (rawParams as any).params && typeof (rawParams as any).params === 'object'
                    ? (rawParams as any).params
                    : rawParams
            );

            if (action === 'mute_mic' || action === 'mute' || action === 'go_to_sleep') {
                if (this.contextService.getSnapshot().anonymous) {
                    this.endConversation();
                    this.sendToolResult(callId, {
                        result: 'success',
                        action: 'end_conversation',
                        message: 'Visit ended. Say "Yo" to start fresh.',
                    });
                    return;
                }
                this.setMicMuted(true);
                this.sendToolResult(callId, { result: 'success', action, message: 'Mic muted. Say "Yo" to resume.' });
                return;
            }
            if (
                action === 'end_conversation' ||
                action === 'end_session' ||
                action === 'end_call' ||
                action === 'finish_conversation'
            ) {
                this.endConversation();
                this.sendToolResult(callId, {
                    result: 'success',
                    action,
                    message: 'Conversation ended. Back at the wake word — say "Yo" to start a new visit.',
                });
                return;
            }

            const beforeSnapshot = this.contextService.getSnapshot();
            const resolved = this.intentRouter.resolvePageAction(beforeSnapshot, action, params);
            const effectiveAction = String(resolved.action ?? action).trim();

            const timeoutMs = this.getDispatchTimeoutMsForAction(effectiveAction);
            const dispatch = await this.dispatchPageActionWithTimeout(effectiveAction, params, timeoutMs);

            const snap = this.contextService.getSnapshot();
            const payload = {
                result: dispatch.ok ? 'success' : 'error',
                action: effectiveAction,
                requested_action: action,
                corrected: resolved.corrected,
                timed_out: dispatch.timedOut,
                pageLabel: String(snap?.pageLabel ?? ''),
                route: String(snap?.route ?? ''),
                availableActions: this.buildAvailableActions(snap),
                dataSummary: this.buildToolResponseState(snap, dispatch.ok),
                message: dispatch.ok
                    ? `Action "${effectiveAction}" executed.`
                    : (dispatch.timedOut
                        ? `Action "${effectiveAction}" timed out while executing.`
                        : resolved.message) ||
                      `Action "${effectiveAction}" is not available here. Use one of availableActions instead.`,
            };

            if (!dispatch.ok) {
                this.addTranscript('assistant', payload.message);
            }
            this.sendToolResult(callId, payload);
        } finally {
            this._toolCallInFlight = false;
            this.syncAiSpeakingState();
        }
    }

    private sendToolResult(callId: string, output: any): void {
        if (!this.isOpen()) return;
        this.send({
            type: 'conversation.item.create',
            item: {
                type: 'function_call_output',
                call_id: callId,
                output: typeof output === 'string' ? output : JSON.stringify(output),
            },
        });
        this.send({ type: 'response.create' });
    }

    private sendClientText(text: string): void {
        if (!this.isOpen()) return;
        this.send({
            type: 'conversation.item.create',
            item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
        });
        this.send({ type: 'response.create' });
    }

    // --- Wake word / greeting / end ---------------------------------------

    private startWakeWordListener(): void {
        this.stopWakeWordListener();
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.warn('[Grok] SpeechRecognition not supported — wake word disabled');
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onresult = (event: any) => {
            if (!this._micMuted.value) return;
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const text = String(event.results[i][0]?.transcript ?? '').toLowerCase().trim();
                if (GrokRealtimeSegment.WAKE_WORDS.some((w) => text.includes(w))) {
                    this.zone.run(() => {
                        this.traceService.log('grok_wake_word', { heard: text }, { detail: `Wake word: "${text}"` });
                        this.contextService.notifyVisitStarted();
                        this.setMicMuted(false);
                        void this.refreshSessionInstructionsBeforeVisit().finally(() => {
                            setTimeout(() => this.requestWakeGreeting(), 500);
                        });
                    });
                    return;
                }
            }
        };
        recognition.onerror = (e: any) => {
            if (e.error === 'no-speech' || e.error === 'aborted') return;
            console.warn('[Grok] SpeechRecognition error:', e.error);
        };
        recognition.onend = () => {
            if (this._status.value === 'active' && this._micMuted.value) {
                try { recognition.start(); } catch {}
            }
        };
        try { recognition.start(); } catch {}
        this._wakeWordRecognition = recognition;
    }

    private stopWakeWordListener(): void {
        if (this._wakeWordRecognition) {
            try { this._wakeWordRecognition.abort(); } catch {}
            this._wakeWordRecognition = null;
        }
    }

    /**
     * Re-fetch the backend system prompt with a fresh page snapshot (full
     * incoming/outgoing order lists) and push it to xAI before the visit
     * greeting. Runs on every "Yo" so the model always sees the current board.
     */
    private async refreshSessionInstructionsBeforeVisit(): Promise<void> {
        if (!this.isOpen()) return;
        try {
            const snapshot = this.contextService.getSnapshot();
            const promptContext = this.contextService.buildPromptContext(snapshot);
            const cfg: any = await this.api
                .postApi('mcp/grok/live-config', {
                    context: {
                        prompt_context: promptContext,
                        route: snapshot.route,
                        page_label: snapshot.pageLabel,
                        user_name: snapshot.userName,
                        user_role: snapshot.role,
                        anonymous: snapshot.anonymous,
                    },
                })
                .toPromise();
            const instructions = String(cfg?.system_instruction ?? '').trim();
            if (instructions) {
                this.send({ type: 'session.update', session: { instructions } });
            }
            // The full board just went in via instructions — baseline the live
            // tracker so we don't immediately re-push the same summary as a
            // redundant board-update item.
            this._lastLiveContextText = String(snapshot.dataSummary ?? '').trim();
            this._lastLiveContextPush = Date.now();
        } catch (err) {
            console.warn('[Grok] session instruction refresh failed:', err);
        }
    }

    /**
     * Subscribe to kiosk context changes for the life of the socket. Each emit
     * is a signal that board data may have changed; pushLiveContextUpdate
     * decides whether/when to actually stream it in.
     */
    private subscribeLiveContext(): void {
        this.unsubscribeLiveContext();
        this._liveContextSub = this.contextService.contextUpdate$.subscribe(() => {
            this.zone.run(() => this.pushLiveContextUpdate());
        });
    }

    private unsubscribeLiveContext(): void {
        if (this._liveContextSub) {
            try { this._liveContextSub.unsubscribe(); } catch {}
            this._liveContextSub = null;
        }
        if (this._liveContextTimer) {
            clearTimeout(this._liveContextTimer);
            this._liveContextTimer = null;
        }
    }

    /**
     * Stream the freshest board into the LIVE conversation when kiosk data
     * changes mid-visit (order received/shipped, new arrival, poller refresh).
     *
     * Injected as a silent `system` conversation item — deliberately NOT
     * `session.update` (that replaces the whole system prompt and wipes the
     * agent's identity/rules) and NOT followed by `response.create` (that would
     * make the model speak unprompted). Only runs while the mic is live (an
     * actual visit); before wake word the full board is re-injected on "Yo".
     * Deferred while a turn is in flight and throttled so a burst of changes
     * collapses into one push.
     */
    private pushLiveContextUpdate(): void {
        if (!this.isOpen() || this._micMuted.value) {
            return;
        }
        // Don't splice a system item into an in-flight exchange; retry after it settles.
        if (this._userTurnActive || this._activeResponses > 0) {
            this.scheduleLiveContextRetry(GrokRealtimeSegment.LIVE_CONTEXT_MIN_INTERVAL_MS);
            return;
        }
        const elapsed = Date.now() - this._lastLiveContextPush;
        if (elapsed < GrokRealtimeSegment.LIVE_CONTEXT_MIN_INTERVAL_MS) {
            this.scheduleLiveContextRetry(GrokRealtimeSegment.LIVE_CONTEXT_MIN_INTERVAL_MS - elapsed);
            return;
        }
        const snapshot = this.contextService.getSnapshot();
        const summary = String(snapshot.dataSummary ?? '').trim();
        if (!summary || summary === this._lastLiveContextText) {
            return;
        }
        this.send({
            type: 'conversation.item.create',
            item: {
                type: 'message',
                role: 'system',
                content: [
                    {
                        type: 'input_text',
                        text: `[BOARD UPDATE — kiosk data changed since your last snapshot; use this going forward]\n${summary}`,
                    },
                ],
            },
        });
        this._lastLiveContextPush = Date.now();
        this._lastLiveContextText = summary;
    }

    private scheduleLiveContextRetry(delay: number): void {
        if (this._liveContextTimer) {
            return;
        }
        this._liveContextTimer = setTimeout(() => {
            this._liveContextTimer = null;
            this.zone.run(() => this.pushLiveContextUpdate());
        }, Math.max(250, delay));
    }

    private requestWakeGreeting(): void {
        if (!this.isOpen()) return;
        // xAI extension: force_message speaks a hard-coded line via TTS with no
        // model round-trip, so the greeting is instant and guaranteed (confirms
        // to the operator that we're listening). Do NOT send response.create —
        // the force_message IS the turn. We rotate a few lines for variety.
        const greetings = [
            'Hey, how can I help?',
            "Hey there — what's up?",
            'Hey! What do you need?',
            "Hi — what can I do for you?",
        ];
        const text = greetings[Math.floor(Math.random() * greetings.length)];
        this._aiSpeaking.next(true);
        this.send({
            type: 'conversation.item.create',
            item: {
                type: 'force_message',
                role: 'assistant',
                interruptible: true,
                content: [{ type: 'output_text', text }],
            },
        });
    }

    private endConversation(): void {
        this.contextService.notifyVisitEnded();
        this._anonymousUserTurns = 0;
        this._requireWakeWordOnConnect = true;
        this.cancelSilenceNudge();
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        setTimeout(() => {
            this._transcript.next([]);
            this._assistantTurnText = '';
            this._activeResponses = 0;
            this.reconnect().catch((err) => console.error('[Grok] end-of-conversation reconnect failed:', err));
        }, 1600);
    }

    // --- Reconnect / refresh / nudge --------------------------------------

    private teardown(): void {
        this._connectAttemptId++;
        this.clearConnectTimeout();
        this.cancelSilenceNudge();
        this.clearResponseFallback();
        this.cancelSessionRefresh();
        this.unsubscribeLiveContext();
        this.stopWakeWordListener();
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this._micStartTimer) {
            clearTimeout(this._micStartTimer);
            this._micStartTimer = null;
        }
        try { this._ws?.close(); } catch {}
        this._ws = null;
        this.stopMicStreaming();
        this.closeOutputAudioContext();
        this._activeResponses = 0;
        this._userTurnActive = false;
        this._aiSpeaking.next(false);
        this.resetUserSpeaking();
        this._status.next('idle');
    }

    private async reconnect(): Promise<void> {
        const wasMuted = this._micMuted.value;
        this._pendingUnmuteAfterReconnect = !wasMuted;
        this.teardown();
        this._autoReconnect = true;
        await this.start();
    }

    private scheduleAutoReconnect(): void {
        if (this._reconnectTimer) return;
        this._pendingUnmuteAfterReconnect = !this._micMuted.value;
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            if (this._status.value === 'active' || this._status.value === 'connecting') return;
            this._autoReconnect = true;
            this.start().catch((err) => console.error('[Grok] auto-reconnect failed:', err));
        }, 1500);
    }

    private armSessionRefresh(): void {
        this.cancelSessionRefresh();
        this._sessionRefreshTimer = setTimeout(() => {
            if (this._status.value !== 'active') return;
            this.addTranscript('system', 'Refreshing session...');
            this.reconnect().catch((err) => console.error('[Grok] session refresh failed:', err));
        }, GrokRealtimeSegment.SESSION_REFRESH_MS);
    }

    private cancelSessionRefresh(): void {
        if (this._sessionRefreshTimer) {
            clearTimeout(this._sessionRefreshTimer);
            this._sessionRefreshTimer = null;
        }
    }

    private armSilenceNudge(): void {
        if (this._toolCallInFlight || this.isAgentBusy()) {
            return;
        }
        this.cancelSilenceNudge();
        this._silenceNudgeTimer = setTimeout(() => {
            this._silenceNudgeTimer = null;
            if (this._status.value !== 'active') return;
            const sinceModel = Date.now() - this._lastModelResponseAt;
            if (sinceModel < GrokRealtimeSegment.SILENCE_NUDGE_MS) return;
            this._silenceNudgeCount++;
            if (this._silenceNudgeCount <= GrokRealtimeSegment.MAX_SILENCE_NUDGES && this.isOpen()) {
                this.sendClientText(
                    'You went silent. In one short sentence, tell the user what you just tried, then ask how to continue. Do NOT stay silent.',
                );
                this.armSilenceNudge();
            } else if (this._silenceNudgeCount > GrokRealtimeSegment.MAX_SILENCE_NUDGES) {
                // Force full recovery: land back in listening (not asleep).
                this._pendingUnmuteAfterReconnect = !this._micMuted.value;
                this.reconnect().catch(() => {});
            }
        }, GrokRealtimeSegment.SILENCE_NUDGE_MS);
    }

    private cancelSilenceNudge(): void {
        if (this._silenceNudgeTimer) {
            clearTimeout(this._silenceNudgeTimer);
            this._silenceNudgeTimer = null;
        }
        this._silenceNudgeCount = 0;
    }

    /**
     * After the server commits the user's audio turn, server_vad should create
     * a response on its own. If it doesn't (no response.created within the
     * window) and nothing else is in flight, request one explicitly so the
     * agent never goes silent after hearing the user.
     */
    private scheduleResponseFallback(): void {
        this.clearResponseFallback();
        this._responseFallbackTimer = setTimeout(() => {
            this._responseFallbackTimer = null;
            if (!this.isOpen()) return;
            if (this._activeResponses > 0 || this._toolCallInFlight) return;
            this.send({ type: 'response.create' });
        }, 700);
    }

    private clearResponseFallback(): void {
        if (this._responseFallbackTimer) {
            clearTimeout(this._responseFallbackTimer);
            this._responseFallbackTimer = null;
        }
    }

    // --- Tool dispatch helpers --------------------------------------------

    private async dispatchPageActionWithTimeout(
        action: string,
        params: Record<string, any>,
        timeoutMs: number
    ): Promise<{ ok: boolean; timedOut: boolean }> {
        let timedOut = false;
        const dispatchPromise = this.pageActionService
            .dispatch(action, params)
            .then((ok) => ({ ok, timedOut: false }))
            .catch(() => ({ ok: false, timedOut: false }));
        const timeoutPromise = new Promise<{ ok: boolean; timedOut: true }>((resolve) => {
            setTimeout(() => {
                timedOut = true;
                resolve({ ok: false, timedOut: true });
            }, timeoutMs);
        });
        return Promise.race([dispatchPromise, timeoutPromise]);
    }

    private getDispatchTimeoutMsForAction(action: string): number {
        const normalized = String(action ?? '').trim().toLowerCase();
        const slow = new Set(['search', 'refresh', 'set_search', 'open_receiving_order', 'open_order']);
        return slow.has(normalized)
            ? GrokRealtimeSegment.TOOL_DISPATCH_SLOW_TIMEOUT_MS
            : GrokRealtimeSegment.TOOL_DISPATCH_TIMEOUT_MS;
    }

    private buildAvailableActions(snapshot: any): string[] {
        const actions = Array.isArray(snapshot?.actions) ? snapshot.actions : [];
        const names: string[] = [];
        const seen = new Set<string>();
        for (const a of actions) {
            const name = String(a?.action ?? '').trim();
            if (!name || seen.has(name)) continue;
            seen.add(name);
            names.push(name);
            if (names.length >= 60) break;
        }
        return names;
    }

    private buildToolResponseState(snapshot: any, ok: boolean): string {
        const full = String(snapshot?.dataSummary ?? '');
        if (!snapshot?.anonymous) {
            return full;
        }
        // Kiosk: return the live board summary (full order lists, matches,
        // open panel) so find_orders / open_order tool results are actionable.
        if (full) {
            return full.length > 6000 ? full.slice(0, 6000) + '…' : full;
        }
        const focus = snapshot?.focus;
        if (focus?.id) {
            const lane = focus?.lane ? ` (${focus.lane})` : '';
            return `Panel open: #${focus.id} "${focus.name || ''}"${lane}.`;
        }
        if (!ok && full) {
            return full.length > 600 ? full.slice(0, 600) + '…' : full;
        }
        return 'No panel open.';
    }

    private maybeRefreshKioskSession(): void {
        if (this._toolCallInFlight || this.isAgentBusy()) return;
        if (this._anonymousUserTurns < GrokRealtimeSegment.KIOSK_TURN_REFRESH) return;
        this._anonymousUserTurns = 0;
        this.traceService.log(
            'grok_kiosk_turn_refresh',
            {},
            { detail: 'Refreshing kiosk session after several user turns' },
        );
        this.reconnect().catch((err) => console.error('[Grok] kiosk turn refresh failed:', err));
    }

    private flattenActionParamWrappers(input: Record<string, any>): Record<string, any> {
        const wrappers = ['actionParams', 'params', 'parameters', 'arguments', 'input'];
        let current: Record<string, any> = { ...(input || {}) };
        for (let i = 0; i < 4; i++) {
            const keys = Object.keys(current);
            if (keys.length === 1) {
                const key = keys[0];
                const value = current[key];
                if (wrappers.includes(key) && value && typeof value === 'object' && !Array.isArray(value)) {
                    current = { ...value };
                    continue;
                }
            }
            break;
        }
        wrappers.forEach((key) => {
            const value = current[key];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                current = { ...value, ...current };
                delete current[key];
            }
        });
        return current;
    }

    // --- Audio I/O ---------------------------------------------------------

    private async startMicStreaming(): Promise<void> {
        if (!this.isOpen()) return;
        if (this._micStream) return;
        // xAI applies server-side noise suppression + echo detection (visible in
        // the session.updated echo as enable_noise_suppression=true /
        // enable_echo_detection_filtering=false with a 3000ms threshold). Layering
        // browser-side noiseSuppression + autoGainControl on top can attenuate a
        // quiet talker below server VAD threshold, which we suspect contributes
        // to speech_started never firing. Keep echoCancellation:true so the
        // greeting audio bleeding through the speaker into the mic still gets
        // cancelled — otherwise VAD would keep triggering on the assistant's
        // own voice during handoff.
        this._micStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: false },
        });
        this.traceService.log('grok_mic_started', {}, { detail: 'Microphone stream acquired for Grok Live' });
        this._inputAudioCtx = new AudioContext({ sampleRate: 48000 });
        if (this._inputAudioCtx.state === 'suspended') {
            await this._inputAudioCtx.resume();
        }
        this._inputSource = this._inputAudioCtx.createMediaStreamSource(this._micStream);
        this._inputProcessor = this._inputAudioCtx.createScriptProcessor(4096, 1, 1);
        let framesSent = 0;
        this._inputProcessor.onaudioprocess = (event) => {
            if (!this.isOpen() || this._micMuted.value) {
                return;
            }
            const channel = event.inputBuffer.getChannelData(0);

            // === LLM path (unchanged) — encode + ship to xAI. Order-of-ops is
            // preserved exactly; the RMS scan below only READS the same buffer.
            const pcm16 = this.floatTo16BitPCM(
                this.resample(channel, this._inputAudioCtx!.sampleRate, GrokRealtimeSegment.INPUT_SAMPLE_RATE)
            );
            const base64 = this.int16ArrayToBase64(pcm16);
            this.send({ type: 'input_audio_buffer.append', audio: base64 });
            framesSent++;

            // === UI path — compute one RMS from the ALREADY-CAPTURED buffer,
            // apply hysteresis, emit only on state transitions. Zero extra Web
            // Audio nodes, zero timers. On any modern CPU this is a few
            // thousand multiplies/sec — well below noise floor.
            let sumSq = 0;
            for (let i = 0; i < channel.length; i++) {
                const v = channel[i];
                sumSq += v * v;
            }
            const rms = Math.sqrt(sumSq / channel.length);
            this.updateUserSpeaking(rms);

            // Diagnostic: on frame 1 (capture online) and then every ~1 s
            // (frame 12 * N) log the raw RMS so we can see how loud your mic
            // actually is when you talk. If you see 0.001–0.005 while
            // speaking, the mic is very quiet and USER_SPEAKING_ON_RMS needs
            // to drop further; if you see 0.02+, we're fine.
            if (framesSent === 1) {
                let peak = 0;
                for (let i = 0; i < channel.length; i++) {
                    const a = channel[i] < 0 ? -channel[i] : channel[i];
                    if (a > peak) peak = a;
                }
                console.log(
                    '[Grok] mic capture online — peak:', peak.toFixed(4),
                    'rms:', rms.toFixed(4),
                    '(ctx', this._inputAudioCtx?.state + ')',
                );
            } else if (framesSent % 12 === 0) {
                console.log('[Grok] mic rms:', rms.toFixed(4), 'speaking:', this._userSpeaking.value);
            }
        };
        this._inputSource.connect(this._inputProcessor);
        this._inputProcessor.connect(this._inputAudioCtx.destination);
    }

    /**
     * Debounced RMS → boolean gate for the kiosk card pulse. Runs on the audio
     * thread (called from onaudioprocess) but only touches integer counters +
     * emits on state TRANSITIONS, so Angular change detection fires a handful
     * of times per turn instead of ~12 Hz per frame. NgZone reconciliation
     * happens inside setUserSpeaking().
     */
    private updateUserSpeaking(rms: number): void {
        if (rms >= GrokRealtimeSegment.USER_SPEAKING_ON_RMS) {
            this._userSpeakingQuietFrames = 0;
            this._userSpeakingLoudFrames++;
            if (
                !this._userSpeaking.value &&
                this._userSpeakingLoudFrames >= GrokRealtimeSegment.USER_SPEAKING_ON_FRAMES
            ) {
                this.setUserSpeaking(true);
            }
        } else if (rms <= GrokRealtimeSegment.USER_SPEAKING_OFF_RMS) {
            this._userSpeakingLoudFrames = 0;
            this._userSpeakingQuietFrames++;
            if (
                this._userSpeaking.value &&
                this._userSpeakingQuietFrames >= GrokRealtimeSegment.USER_SPEAKING_OFF_FRAMES
            ) {
                this.setUserSpeaking(false);
            }
        } else {
            // Between the two thresholds → hold current state, don't count
            // either way. This is the hysteresis dead-band that stops flicker.
            this._userSpeakingLoudFrames = 0;
            this._userSpeakingQuietFrames = 0;
        }
    }

    private setUserSpeaking(next: boolean): void {
        if (this._userSpeaking.value === next) return;
        // Re-enter Angular so [attr.data-heard] updates trigger change
        // detection on the kiosk agent HUD. Cheap: fires only on transitions.
        this.zone.run(() => this._userSpeaking.next(next));
    }

    private resetUserSpeaking(): void {
        this._userSpeakingLoudFrames = 0;
        this._userSpeakingQuietFrames = 0;
        this.setUserSpeaking(false);
    }

    private stopMicStreaming(): void {
        if (this._inputProcessor) {
            try { this._inputProcessor.disconnect(); } catch {}
            this._inputProcessor.onaudioprocess = null as any;
            this._inputProcessor = null;
        }
        if (this._inputSource) {
            try { this._inputSource.disconnect(); } catch {}
            this._inputSource = null;
        }
        if (this._inputAudioCtx) {
            try { this._inputAudioCtx.close(); } catch {}
            this._inputAudioCtx = null;
        }
        if (this._micStream) {
            this._micStream.getTracks().forEach((t) => t.stop());
            this._micStream = null;
        }
    }

    private ensureOutputAudioContext(): void {
        if (!this._outputAudioCtx) {
            this._outputAudioCtx = new AudioContext({ sampleRate: GrokRealtimeSegment.OUTPUT_SAMPLE_RATE });
            this._nextPlaybackTime = this._outputAudioCtx.currentTime;
        }
        if (this._outputAudioCtx.state === 'suspended') {
            this._outputAudioCtx.resume();
        }
    }

    private closeOutputAudioContext(): void {
        if (!this._outputAudioCtx) return;
        try { this._outputAudioCtx.close(); } catch {}
        this._outputAudioCtx = null;
        this._nextPlaybackTime = 0;
        if (this._playbackEndTimer) {
            clearTimeout(this._playbackEndTimer);
            this._playbackEndTimer = null;
        }
    }

    private playPcm16Base64(base64Data: string, sampleRate: number): void {
        this.ensureOutputAudioContext();
        if (!this._outputAudioCtx) return;
        const bytes = this.base64ToUint8Array(base64Data);
        const sampleCount = Math.floor(bytes.byteLength / 2);
        if (sampleCount <= 0) return;
        const audioBuffer = this._outputAudioCtx.createBuffer(1, sampleCount, sampleRate);
        const channel = audioBuffer.getChannelData(0);
        for (let i = 0; i < sampleCount; i++) {
            const lo = bytes[i * 2];
            const hi = bytes[i * 2 + 1];
            let val = (hi << 8) | lo;
            if (val >= 0x8000) val = val - 0x10000;
            channel[i] = Math.max(-1, Math.min(1, val / 32768));
        }
        const src = this._outputAudioCtx.createBufferSource();
        src.buffer = audioBuffer;
        src.connect(this._outputAudioCtx.destination);
        const now = this._outputAudioCtx.currentTime;
        const when = Math.max(now, this._nextPlaybackTime);
        src.start(when);
        this._nextPlaybackTime = when + audioBuffer.duration;
        this._aiSpeaking.next(true);
        this.schedulePlaybackEndCheck();
    }

    private isAgentBusy(): boolean {
        if (this._toolCallInFlight || this._activeResponses > 0) {
            return true;
        }
        if (this._outputAudioCtx) {
            const busyUntil = this._nextPlaybackTime + GrokRealtimeSegment.POST_PLAYBACK_GAP_S;
            if (this._outputAudioCtx.currentTime < busyUntil) {
                return true;
            }
        }
        return false;
    }

    private schedulePlaybackEndCheck(): void {
        if (this._playbackEndTimer) {
            clearTimeout(this._playbackEndTimer);
            this._playbackEndTimer = null;
        }
        if (!this._outputAudioCtx) {
            return;
        }
        const ms = Math.max(
            0,
            (this._nextPlaybackTime +
                GrokRealtimeSegment.POST_PLAYBACK_GAP_S -
                this._outputAudioCtx.currentTime) *
                1000,
        );
        this._playbackEndTimer = setTimeout(() => {
            this._playbackEndTimer = null;
            this.syncAiSpeakingState();
        }, ms + 20);
    }

    private syncAiSpeakingState(): void {
        const speaking = this.isAgentBusy() && !this._micMuted.value;
        this._aiSpeaking.next(speaking);
    }

    private playMuteTone(): void {
        try {
            const ctx = new AudioContext();
            const t = ctx.currentTime;
            const gain = ctx.createGain();
            gain.connect(ctx.destination);
            gain.gain.setValueAtTime(0.3, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(660, t);
            osc.frequency.exponentialRampToValueAtTime(440, t + 0.15);
            osc.connect(gain);
            osc.start(t);
            osc.stop(t + 0.2);
            setTimeout(() => ctx.close().catch(() => {}), 500);
        } catch {}
    }

    private resample(input: Float32Array, inputRate: number, targetRate: number): Float32Array {
        if (inputRate === targetRate) return input;
        const ratio = inputRate / targetRate;
        const newLength = Math.round(input.length / ratio);
        const result = new Float32Array(newLength);
        let offsetResult = 0;
        let offsetBuffer = 0;
        while (offsetResult < result.length) {
            const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
            let accum = 0;
            let count = 0;
            for (let i = offsetBuffer; i < nextOffsetBuffer && i < input.length; i++) {
                accum += input[i];
                count++;
            }
            result[offsetResult] = count > 0 ? accum / count : 0;
            offsetResult++;
            offsetBuffer = nextOffsetBuffer;
        }
        return result;
    }

    private floatTo16BitPCM(input: Float32Array): Int16Array {
        const out = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return out;
    }

    private int16ArrayToBase64(data: Int16Array): string {
        const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    private base64ToUint8Array(base64: string): Uint8Array {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    }

    // --- Misc --------------------------------------------------------------

    private isOpen(): boolean {
        return !!this._ws && this._ws.readyState === WebSocket.OPEN && this._status.value === 'active';
    }

    private send(payload: any): void {
        if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
        try {
            this._ws.send(JSON.stringify(payload));
        } catch (err) {
            console.error('[Grok] send error:', err);
        }
    }

    private upsertAssistantTurn(text: string): void {
        const t = [...this._transcript.value];
        for (let i = t.length - 1; i >= 0; i--) {
            if (t[i].role === 'assistant') {
                t[i] = { ...t[i], text };
                this._transcript.next(t);
                return;
            }
            if (t[i].role === 'user' || t[i].role === 'system') break;
        }
        this.addTranscript('assistant', text);
    }

    private upsertUserTurn(text: string): void {
        const t = [...this._transcript.value];
        if (this._userTurnActive) {
            for (let i = t.length - 1; i >= 0; i--) {
                if (t[i].role === 'user') {
                    t[i] = { ...t[i], text };
                    this._transcript.next(t);
                    return;
                }
                if (t[i].role === 'assistant') break;
            }
        }
        this._userTurnActive = true;
        this.addTranscript('user', text);
    }

    private addTranscript(role: 'user' | 'assistant' | 'system', text: string): void {
        const next = [...this._transcript.value, { role, text, at: new Date().toISOString() }].slice(-40);
        this._transcript.next(next);
    }

    private armConnectTimeout(attemptId: number): void {
        this.clearConnectTimeout();
        this._connectTimeout = setTimeout(() => {
            if (attemptId !== this._connectAttemptId || this._status.value !== 'connecting') return;
            try { this._ws?.close(); } catch {}
            this._ws = null;
            this._status.next('error');
            this.addTranscript('system', 'Grok timed out while connecting.');
        }, GrokRealtimeSegment.CONNECT_TIMEOUT_MS);
    }

    private clearConnectTimeout(): void {
        if (!this._connectTimeout) return;
        clearTimeout(this._connectTimeout);
        this._connectTimeout = null;
    }

    private canStartOnCurrentPage(snapshot: any): boolean {
        const pageLabel = String(snapshot?.pageLabel ?? '').trim().toLowerCase();
        const route = String(snapshot?.route ?? '').toLowerCase();
        const actionCount = Array.isArray(snapshot?.actions) ? snapshot.actions.length : 0;
        if (actionCount > 0) return true;
        if (route.includes('/kiosk/packages')) return true;
        if (pageLabel.includes('package')) return true;
        return false;
    }
}
